import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dropdown, Grid, Message } from 'semantic-ui-react';

import Button from '../../../../components/Button';
import axiosInstance from '../../../../services/axios-instance';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';
import * as Storage from '../../../../services/storage';
import { iRootState } from '../../../../store';
import { FieldLabel } from '../../../../styles';
import {
  ButtonsWrapper,
  ReportSectionWrapper,
  SelectWrapper
} from '../../styles';
import { Props } from './types';

const DEFAULT_XLSX_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function parseFilenameFromContentDisposition(
  contentDisposition?: string
): string | null {
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/["']/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/["']/g, '');
    }
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plainMatch?.[1]?.trim() || null;
}

async function extractBlobErrorMessage(error: unknown): Promise<string> {
  const fallback =
    'Não foi possível exportar o relatório Alfa Modelo Yelum. Tente novamente mais tarde.';

  const response = (error as { response?: { data?: unknown; status?: number } })
    ?.response;
  const data = response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string | string[] };
      if (Array.isArray(parsed.message)) {
        return parsed.message.join(', ');
      }
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      return fallback;
    }
  }

  if (data && typeof data === 'object' && !(data instanceof Blob)) {
    const message = (data as { message?: unknown }).message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

const AlfaYelumModelReport: React.FC<Props> = ({
  closeSection,
  availableBrokersAndEstates
}) => {
  const [selectedEstateId, setSelectedEstateId] = useState<string>('');
  const [isExportLoading, setIsExportLoading] = useState(false);

  const estateOptions = useMemo(
    () =>
      availableBrokersAndEstates.estates?.map(option => ({
        text: option.text,
        value: option.value.id as string
      })) || [],
    [availableBrokersAndEstates.estates]
  );

  const onExportButtonClick = async () => {
    if (!selectedEstateId) {
      warningNotification('Atenção', 'Selecione uma imobiliária para exportar.');
      return;
    }

    setIsExportLoading(true);

    try {
      const response = await axiosInstance.get(
        `/alfa-yelum-export/${selectedEstateId}`,
        {
          headers: {
            'X-External-Login': Storage.get('external_login')
          },
          responseType: 'blob'
        }
      );

      const contentType =
        String(response.headers['content-type'] || '').split(';')[0].trim() ||
        DEFAULT_XLSX_TYPE;
      const filename =
        parseFilenameFromContentDisposition(
          response.headers['content-disposition']
        ) ||
        (contentType.includes('zip')
          ? 'alfa_yelum_export.zip'
          : 'alfa_yelum_export.xlsx');

      const blob = new Blob([response.data], { type: contentType });
      triggerBrowserDownload(blob, filename);

      const processed = response.headers['x-processed'];
      const residential = response.headers['x-residential'];
      const commercial = response.headers['x-commercial'];
      const successDetail =
        processed != null
          ? `Arquivo gerado com ${processed} apólice(s) (${residential || 0} residencial(is), ${commercial || 0} comercial(is)).`
          : 'O relatório Alfa Modelo Yelum foi exportado com sucesso.';

      successNotification('Sucesso!', successDetail);
    } catch (error) {
      const message = await extractBlobErrorMessage(error);
      dangerNotification('Oops...', message);
    } finally {
      setIsExportLoading(false);
    }
  };

  return (
    <ReportSectionWrapper>
      <Message info>
        <Message.Header>Relatório Alfa Modelo Yelum</Message.Header>
        <p>
          Exporta as apólices Alfa vigentes da imobiliária no layout Yelum
          calculoExport. Se houver itens residenciais e comerciais, o download
          será um ZIP com dois arquivos XLSX.
        </p>
      </Message>

      <Grid>
        <Grid.Row style={{ paddingTop: 0, paddingBottom: 0 }}>
          <Grid.Column width={8}>
            <FieldLabel paddingBottom>Imobiliária*</FieldLabel>

            <SelectWrapper style={{ marginBottom: 0 }}>
              <Dropdown
                id="alfaYelumEstateSelect"
                placeholder="Selecione uma imobiliária"
                value={selectedEstateId || undefined}
                options={estateOptions}
                onChange={(_, data) =>
                  setSelectedEstateId((data.value as string) || '')
                }
                style={{ marginBottom: '5px' }}
                menu={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                noResultsMessage="Nenhuma imobiliária encontrada"
                clearable
                fluid
                search
                selection
              />
            </SelectWrapper>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <ButtonsWrapper>
        <Button
          type="button"
          text="Cancelar"
          disabled={isExportLoading}
          onClick={closeSection}
          light
        />

        <Button
          type="button"
          text="Baixar relatório"
          disabled={isExportLoading}
          loading={isExportLoading}
          onClick={() => onExportButtonClick()}
        />
      </ButtonsWrapper>
    </ReportSectionWrapper>
  );
};

const mapStateToProps = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

export default connect(mapStateToProps)(AlfaYelumModelReport);
